import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { DataGrid, GridColumns, GridRenderCellParams } from "@mui/x-data-grid";

import { FeatureGate } from "components/FeatureGate/FeatureGate";
import { permissions } from "constants/constants";
import { useSelectedOrganization } from "hooks/useSelectedOrganization";
import { OrganizationApiToken } from "types/Organization";
import { getPermissionsByOrgId } from "utils/localStorage";
import { AddSettings } from "./AddSettings";
import { DeleteSetting } from "./DeleteSetting";
import { EditSetting } from "./EditSetting";

interface SettingsTabProps {
  token: OrganizationApiToken;
}
export const SettingsTab = ({ token }: SettingsTabProps): JSX.Element => {
  const { t } = useTranslation();
  const { selectedOrganizationId } = useSelectedOrganization();
  const organizationPermissions = getPermissionsByOrgId(selectedOrganizationId);
  const isEditable = useMemo(
    () => (organizationPermissions ?? []).includes(permissions.APITOKEN_UPDATE),
    [organizationPermissions]
  );

  const settings = useMemo(() => {
    if (!token.settings) return [];
    return Object.keys(token.settings).map((key) => ({
      key,
      id: key,
      value: token.settings?.[key],
    }));
  }, [token]);

  const columns: GridColumns = useMemo(() => {
    const columns: GridColumns = [
      {
        field: "key",
        headerName: t("Name"),
        sortable: false,
        flex: 1,
      },
      {
        field: "value",
        headerName: t("Value"),
        sortable: false,
        flex: 1,
      },
    ];
    if (isEditable) {
      columns.push({
        field: "edit",
        headerName: "",
        sortable: false,
        align: "right",
        width: 100,
        renderCell: (
          params: GridRenderCellParams<unknown, Record<string, string>>
        ) => (
          <Box sx={{ ml: "auto" }}>
            <EditSetting row={params.row} token={token} />
            <DeleteSetting row={params.row} token={token} />
          </Box>
        ),
      });
    }
    return columns;
  }, [t, isEditable, token]);
  return (
    <>
      <FeatureGate name={permissions.APITOKEN_UPDATE}>
        <Stack
          direction="row"
          paddingLeft={2}
          paddingY={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography>{t("Settings", { count: settings.length })}</Typography>
          <AddSettings token={token} />
        </Stack>
      </FeatureGate>
      <Paper
        sx={{
          display: "flex",
          flex: 1,
        }}
        elevation={2}
      >
        <DataGrid rows={settings} hideFooter columns={columns} />
      </Paper>
    </>
  );
};
